import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register zoom plugin
ChartJS.register(zoomPlugin);

// Format numbers for display
const formatLargeNumber = (number) => {
    if (number == null || isNaN(number)) return 'N/A';
    const billion = 1000000000;
    const million = 1000000;
    if (Math.abs(number) >= billion) {
        return (number / billion).toFixed(2) + 'B';
    } else if (Math.abs(number) >= million) {
        return (number / million).toFixed(2) + 'M';
    }
    return number.toLocaleString();
};

const formatRoundLargeNumber = (number) => {
    if (number == null || isNaN(number)) return 'N/A';
    const billion = 1000000000;
    const million = 1000000;
    if (Math.abs(number) >= billion) {
        return (number / billion).toFixed(0) + 'B';
    } else if (Math.abs(number) >= million) {
        return (number / million).toFixed(0) + 'M';
    }
    return number.toLocaleString();
};

const FinancialChartModal = ({ 
    open, 
    onClose, 
    metricLabel, 
    chartType,
    filteredData, 
    filteredGrowthData,
    metricKey,
    growthKey,
    periodType 
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Prepare labels
    const labels = filteredData.map((statement) => {
        return periodType === 'annual' 
            ? `FY ${new Date(statement.date).getFullYear()}`
            : `${statement.period} ${new Date(statement.date).getFullYear()}`;
    }).reverse(); // Oldest to newest

    // Prepare data
    const values = filteredData.map(s => s[metricKey]).reverse();
    const growthValues = filteredGrowthData.map(g => g ? (g[growthKey] * 100) : null).reverse();

    // Better color scheme
    const primaryColor = theme.palette.mode === 'dark' 
        ? 'rgba(33, 150, 243, 0.8)'
        : 'rgba(25, 118, 210, 0.85)';
    
    const growthColor = theme.palette.mode === 'dark'
        ? 'rgba(102, 187, 106, 1)'
        : 'rgba(76, 175, 80, 1)';

    // Chart options with zoom enabled
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: isMobile ? 10 : 20,
                    font: {
                        size: isMobile ? 10 : 12
                    }
                }
            },
            tooltip: {
                enabled: true,
                backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(0, 0, 0, 0.9)' 
                    : 'rgba(255, 255, 255, 0.9)',
                titleColor: theme.palette.text.primary,
                bodyColor: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                borderWidth: 1,
                padding: 12,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.raw;
                        
                        if (label.includes('Growth')) {
                            return `${label}: ${value?.toFixed(2)}%`;
                        }
                        return `${label}: $${formatLargeNumber(value)}`;
                    }
                }
            },
            datalabels: {
                display: false
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x',
                    modifierKey: null,  // No modifier key needed
                },
                zoom: {
                    wheel: {
                        enabled: true,
                        speed: 0.1,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                limits: {
                    x: { min: 'original', max: 'original' },
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    maxRotation: isMobile ? 90 : 45,
                    minRotation: isMobile ? 90 : 45,
                    autoSkip: true,
                    maxTicksLimit: labels.length > 15 ? (isMobile ? 8 : 12) : undefined,
                    font: {
                        size: isMobile ? 9 : 11
                    }
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                grid: {
                    display: true,
                    color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                    callback: function (value) {
                        return '$' + formatRoundLargeNumber(value);
                    },
                    font: {
                        size: isMobile ? 9 : 11
                    }
                }
            },
            y1: chartType === 'combo' ? {
                type: 'linear',
                display: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false,
                },
                ticks: {
                    callback: function (value) {
                        return `${value.toFixed(0)}%`;
                    },
                    font: {
                        size: isMobile ? 9 : 11
                    }
                }
            } : undefined
        }
    };

    // Prepare chart data
    const getChartData = () => {
        if (chartType === 'combo') {
            return {
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: metricLabel,
                        data: values,
                        backgroundColor: primaryColor,
                        borderColor: primaryColor,
                        borderWidth: 0,
                        borderRadius: 4,
                        yAxisID: 'y',
                    },
                    {
                        type: 'line',
                        label: 'Growth %',
                        data: growthValues,
                        borderColor: growthColor,
                        backgroundColor: 'transparent',
                        borderWidth: 2.5,
                        pointRadius: isMobile ? 3 : 4,
                        pointBackgroundColor: growthColor,
                        pointBorderColor: '#fff',
                        pointBorderWidth: 1,
                        yAxisID: 'y1',
                        tension: 0.3
                    }
                ]
            };
        } else {
            return {
                labels,
                datasets: [
                    {
                        label: metricLabel,
                        data: values,
                        borderColor: primaryColor,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(33, 150, 243, 0.2)'
                            : 'rgba(25, 118, 210, 0.15)',
                        borderWidth: 2.5,
                        fill: true,
                        tension: 0.3,
                        pointRadius: isMobile ? 3 : 4,
                        pointBackgroundColor: primaryColor,
                        pointBorderColor: '#fff',
                        pointBorderWidth: 1,
                    }
                ]
            };
        }
    };

    const chartData = getChartData();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(19, 47, 76, 0.95)' 
                        : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    m: { xs: 0, sm: 2 }
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pb: { xs: 0.5, sm: 1 },
                pt: { xs: 1.5, sm: 2 },
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '1rem', sm: '1.25rem' }
            }}>
                {metricLabel}
                <IconButton 
                    onClick={onClose} 
                    size="small"
                    sx={{ ml: 2 }}
                >
                    <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ 
                px: { xs: 1, sm: 3 }, 
                pb: { xs: 2, sm: 3 },
                pt: 0
            }}>
                <Box sx={{ 
                    height: { xs: 300, sm: 400, md: 450 }, 
                    width: '100%'
                }}>
                    {chartType === 'combo' ? (
                        <Bar data={chartData} options={chartOptions} />
                    ) : (
                        <Line data={chartData} options={chartOptions} />
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default FinancialChartModal;